'use client';

import { useEffect, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { USER_VARIABLE } from '@nx-frontend/shared/api';
import { clientSendWorkspaceEvent } from '@nx-frontend/shared/api/webnotes/client';
import { useDebounce } from '@nx-frontend/shared/hooks';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $showActivateTrial } from '@nx-frontend/shared/store/trial';
import { $user } from '@nx-frontend/shared/store/user';
import { $userVars, setUserVarEvent } from '@nx-frontend/shared/store/user-vars';
import { BillingInfoPlan, OrgInfoSubscriptionType } from '@nx-frontend/shared/types';
import { getSubscriptionType } from '@nx-frontend/shared/utils';
import { webnotesClientEnv } from '@nx-frontend/webnotes-client/shared';

export const AppMarketingHandler = () => {
  const [showActivateTrial, userVars, user, currentOrganization, billingInfo, orgInfo, orgCoupons] = useUnit([
    $showActivateTrial,
    $userVars,
    $user,
    $currentOrganization,
    $billingInfo,
    $orgInfo,
    $orgCoupons,
  ]);
  const [setUserVar] = useUnit([setUserVarEvent]);

  const firstVisitWsNoPaywall = useMemo(() => {
    return userVars.find((userVar) => userVar.key === USER_VARIABLE.FIRST_VISIT_WS_NO_PAYWALL)?.value;
  }, [userVars]);

  const isTrialWithCard = useMemo(() => {
    if (billingInfo.active && Date.now() / 1000 <= billingInfo.trialEndsAt) {
      return true;
    }
    return false;
  }, [billingInfo]);

  const showTrialStartPopup = useMemo(() => {
    const subscriptionType = getSubscriptionType({ billingInfo, orgInfo, orgCoupons });
    let show = showActivateTrial;
    if (subscriptionType === OrgInfoSubscriptionType.STANDARD) {
      if (billingInfo.plan !== BillingInfoPlan.NOPE) {
        show = false;
      }
    } else {
      show = false;
    }
    return show;
  }, [orgInfo, orgCoupons, billingInfo, showActivateTrial]);

  /**
   * Check if user is visiting the workspace for the first time and not show the activate trial popup
   * Then will be send first_visit event to Esputnik
   */
  const checkFirstVisitDebounce = useDebounce(
    (params: {
      userId: number;
      firstVisitWsNoPaywall: boolean | undefined | unknown;
      remoteApplication: string;
      currentOrgUserId: number;
      isTrialWithCard: boolean;
      showTrialStartPopup: boolean;
    }) => {
      const { firstVisitWsNoPaywall, remoteApplication, userId, currentOrgUserId } = params;
      const isWorkspace = !!document.location.pathname.match(/space\/.*?\/all$/);
      const isCheckFirstVisit =
        user.registerDate >= webnotesClientEnv.checkFirstVisitTimestamp && userId === currentOrgUserId;
      if (
        isCheckFirstVisit &&
        isWorkspace &&
        (!showTrialStartPopup || isTrialWithCard) &&
        !firstVisitWsNoPaywall &&
        !['screens_chrome', 'screens_ff'].includes(remoteApplication)
      ) {
        clientSendWorkspaceEvent({ name: 'workspaces:firstVisitNoPaywall' });
        setUserVar({ key: USER_VARIABLE.FIRST_VISIT_WS_NO_PAYWALL, value: true });
      }
    },
    1000
  );

  useEffect(() => {
    checkFirstVisitDebounce({
      userId: user.id,
      firstVisitWsNoPaywall,
      remoteApplication: user.remoteApplication || '',
      currentOrgUserId: currentOrganization.userId,
      isTrialWithCard,
      showTrialStartPopup,
    });
  }, [
    user.id,
    firstVisitWsNoPaywall,
    checkFirstVisitDebounce,
    user.remoteApplication,
    currentOrganization,
    isTrialWithCard,
    showTrialStartPopup,
  ]);

  return null;
};
